import React, { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import Button from '../../../shared/SmallComponents/Button';
import CustomModal from '../../../shared/Modal/Modal';
import { shipment } from '../../../../models/entities/shipment/shipment';
import { businessPartner } from '../../../../models/entities/businessPartner/businessPartner';
import { bpAccountingResponse, bpAccountingRow } from '../../../../models/entities/businessPartner/accounting';
import { businessPartnersSelectors } from '../../../../state/ducks/businessPartner/selectors';
import { singleCompanySelectors } from '../../../../state/ducks/singleCompany/selectors';
import { shipmentsSelectors } from '../../../../state/ducks/shipments/selectors';
import { localizationSelectors } from '../../../../state/ducks/localization/selectors';
import { RootState } from '../../../../state/store/store';
import SendInvoiceModal from '../../businessPartners/singleBusinessPartner/sections/Accounting/SendInvoiceModal';
import { buildBpInvoiceIssuer } from '../../businessPartners/singleBusinessPartner/sections/Accounting/invoicePdfTypes';

type Props = {
    shipment: shipment;
    onInvoiceSent?: () => void;
    businessPartner?: businessPartner;
    issuerCompany?: {
        name?: string;
        address?: string;
        phoneNumber?: string;
        vatNumber?: number | string;
        logoUrl?: string;
    };
    shipmentSettings?: ReturnType<ReturnType<typeof shipmentsSelectors.settingsByLanguage>>;
    accountingLocalization?: {
        send_invoice: string;
        send_modal: {
            title: string;
            contacts_placeholder: string;
            send_btn: string;
            includes_documents: string;
            no_financial_contacts: string;
            success: string;
            error: string;
        };
    };
};

const DEFAULT_SEND_MODAL = {
    title: 'Send invoice & statement',
    contacts_placeholder: 'Select recipients',
    send_btn: 'Send email',
    includes_documents:
        'Includes all non-internal documents from the Documents tab plus account statement.',
    no_financial_contacts:
        'No contacts are marked for financial documents. Select recipients below or update Contacts.',
    success: 'Invoice package sent successfully.',
    error: 'Failed to send invoice package.'
};

const buildRowFromShipment = (shipment: shipment, currency: string): bpAccountingRow => ({
    shipmentId: shipment.id,
    shipmentNumber: shipment.CargoZoneNumber,
    shipmentDate: shipment.createdAt ? String(shipment.createdAt) : null,
    totalCost: 0,
    paid: 0,
    openBalance: 0,
    profit: 0,
    profitByCurrency: {},
    dueDate: null,
    agingDays: null,
    invoicePaid: false,
    accountingState: shipment.accountingState ?? null,
    bucket: 'OPEN',
    currency
});

const buildMinimalAccountingData = (
    row: bpAccountingRow,
    businessPartner: businessPartner
): bpAccountingResponse => ({
    rows: [row],
    ledgerEntries: [],
    kpis: {
        currency: row.currency,
        totalOpenBalance: row.openBalance,
        overdueAmount: 0,
        overdueShipmentCount: 0,
        dueSoonAmount: 0,
        dueSoonShipmentCount: 0,
        paidLast60: 0,
        creditLine: 0,
        availableCredit: 0,
        paymentTermsLabel: businessPartner.paymentTerms ?? null,
        profitByCurrency: row.profitByCurrency,
        totalCostByCurrency: {},
        profitSurchargePercent: 0
    }
});

const ShipmentFinancialActions = ({
    shipment,
    onInvoiceSent,
    businessPartner,
    issuerCompany,
    shipmentSettings,
    accountingLocalization
}: Props) => {
    const [sendOpen, setSendOpen] = useState(false);

    const cargoOwnerId = shipment.cargoOwnerId;
    const currency = businessPartner?.baseCurrency?.trim().toUpperCase() || 'USD';
    const hasBillingLines = (shipment.billingLines?.length ?? 0) > 0;

    const row = useMemo(() => buildRowFromShipment(shipment, currency), [shipment, currency]);

    const modalAccountingData = useMemo(() => {
        if (!businessPartner) return null;
        return buildMinimalAccountingData(row, businessPartner);
    }, [businessPartner, row]);

    const issuer = useMemo(() => buildBpInvoiceIssuer(issuerCompany), [issuerCompany]);

    if (!cargoOwnerId || !businessPartner) return null;

    const loc = accountingLocalization;
    const sendModalLoc = loc?.send_modal ?? DEFAULT_SEND_MODAL;

    return (
        <>
            <Button
                buttonType="hollow"
                padding="4px 12px"
                disabled={!hasBillingLines}
                onClick={() => setSendOpen(true)}
            >
                {loc?.send_invoice || 'Send invoice'}
            </Button>
            {sendOpen && modalAccountingData && (
                <CustomModal
                    isOpen={true}
                    title={{ text: sendModalLoc.title }}
                    width="480px"
                    onCancelCallback={() => setSendOpen(false)}
                    Content={() => (
                        <SendInvoiceModal
                            businessPartner={businessPartner}
                            row={row}
                            accountingData={modalAccountingData}
                            issuer={issuer}
                            shipmentSettings={shipmentSettings}
                            localization={{ send_modal: sendModalLoc }}
                            onClose={() => setSendOpen(false)}
                            onSuccess={() => setSendOpen(false)}
                            onInvoiceSent={onInvoiceSent}
                        />
                    )}
                />
            )}
        </>
    );
};

const mapStateToProps = (state: RootState, ownProps: { shipment: shipment }) => {
    const pageLoc = localizationSelectors.SingleBusinessPartnerPage(state);
    return {
        businessPartner: ownProps.shipment.cargoOwnerId
            ? businessPartnersSelectors.getById(state, ownProps.shipment.cargoOwnerId)
            : undefined,
        issuerCompany: singleCompanySelectors.company(state),
        shipmentSettings: shipmentsSelectors.settingsByLanguage(state)(),
        accountingLocalization: pageLoc?.accounting
    };
};

export default connect(mapStateToProps)(ShipmentFinancialActions);
